import { useMemo, useState } from "react";
import { AlertTriangle, Search, Image as ImageIcon } from "lucide-react";

const f1 = (v) => (v == null ? "—" : v.toFixed(1));
const f3 = (v) => (v == null ? "—" : v.toFixed(3));
const STATS = [["pts", "PTS"], ["reb", "REB"], ["ast", "AST"], ["fg3m", "3PM"], ["pra", "PRA"]];
const SORTABLE = { min: (p) => p.minutes ?? -1, gp: (p) => p.profile?.sample_games ?? -1, pts: (p) => p.projection?.pts ?? -1, reb: (p) => p.projection?.reb ?? -1, ast: (p) => p.projection?.ast ?? -1, fg3m: (p) => p.projection?.fg3m ?? -1, pra: (p) => p.projection?.pra ?? -1 };

function seedText(meta) {
  if (!meta || meta.seed == null) return null;
  const conf = (meta.conference || "").startsWith("West") ? "West" : "East";
  const n = meta.seed;
  const sfx = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
  return `${n}${sfx} ${conf}`;
}

function PlayerRow({ p, details }) {
  const pr = p.profile || {};
  return (
    <tr className={p.out ? "is-out" : ""} data-testid={`sm-player-${p.player_id}`}>
      <td className="l">
        <div className="smx-pl">
          {p.photo ? <img src={p.photo} alt="" referrerPolicy="no-referrer" /> : <span className="smx-pl-ph"><ImageIcon size={13} /></span>}
          <div>
            <b>{p.name}</b>
            <small>{p.position || "—"}{p.out ? " · OUT" : ""}{p.override || p.manual ? " · fixed min" : ""}</small>
          </div>
        </div>
      </td>
      <td>{p.position || "—"}</td>
      <td><span className={`smx-rolechip ${p.starter ? "s" : "b"}`}>{p.starter ? "Starter" : "Bench"}</span>
        {p.flag === "promoted" && <span className="smx-flag">promoted</span>}
        {p.stability && !p.stability.same_role_all && p.stability.observed_games > 0 && <span className="smx-flag" title={`Last roles: ${p.stability.roles.map((r) => (r ? "S" : "B")).join(" ")}`}>role change</span>}
      </td>
      <td>{pr.sample_games ?? "—"}</td>
      <td>{f1(p.minutes)}</td>
      <td>{pr.target_band || "—"} → {pr.source_band || "—"}{pr.fallback_role && <span className="smx-flag" title="any role fallback">any</span>}</td>
      {details && ["pts", "reb", "ast", "fg3m"].map((m) => <td key={m} className="smx-x4-cell">{pr.rates ? f3(pr.rates[m]) : "—"}</td>)}
      {STATS.map(([k]) => (
        <td key={k}>
          {p.projection ? <b>{f1(p.projection[k])}</b> : <span className="smx-na" title={pr.reason}>n/a</span>}
          {details && <span className="smx-x4">x4 {f1(p.x4?.[k])}</span>}
        </td>
      ))}
    </tr>
  );
}

export function TeamModel({ t, meta, oppCode, home, details, onToggleDetails }) {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "min", dir: -1 });

  const players = useMemo(() => t.players || [], [t.players]);
  const counts = useMemo(() => ({ starters: players.filter((p) => p.starter).length, bench: players.filter((p) => !p.starter).length }), [players]);

  const shown = useMemo(() => {
    let rows = players.filter((p) => (tab === "all" || (tab === "starters" ? p.starter : !p.starter)) && (!q || (p.name || "").toLowerCase().includes(q.toLowerCase())));
    const get = SORTABLE[sort.key] || SORTABLE.min;
    return [...rows].sort((a, b) => (get(a) - get(b)) * sort.dir);
  }, [players, tab, q, sort]);

  const setSortKey = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: -1 }));

  if (t.status === "unavailable") {
    return (
      <section className="smx-team" data-testid={`sm-team-${t.team_code}`}>
        <div className="smx-team-top" style={{ gridTemplateColumns: "1fr" }}>
          <div className="smx-identity">
            {meta?.logo ? <img src={meta.logo} alt="" /> : <span className="smx-crest-fallback">{t.team_code}</span>}
            <div><span className="smx-abbr">{t.team_code}</span><span className="smx-full">{meta?.name || ""}</span></div>
          </div>
        </div>
        <p className="smx-warn" style={{ padding: "0 16px 14px" }}><AlertTriangle size={14} />{t.reason}</p>
      </section>
    );
  }

  const ps = t.proj_split || {}; const hs = t.hist_split || {};
  const stMin = ps.starter_minutes || 0; const bnMin = ps.bench_minutes || 0; const tot = stMin + bnMin || 1;
  const min240ok = Math.abs((t.minutes_total ?? 0) - 240) < 0.5;
  const rec = meta ? `${meta.wins ?? 0}-${meta.losses ?? 0}` : null;
  const seed = seedText(meta);

  return (
    <section className="smx-team" style={{ "--team": `#${t.team_color || "1f9bff"}` }} data-testid={`sm-team-${t.team_code}`}>
      <div className="smx-team-top">
        <button type="button" className="smx-identity" onClick={() => meta && window.open(`/teams/${t.team_id}`, "_self")} data-testid={`sm-identity-${t.team_code}`}>
          {meta?.logo ? <img src={meta.logo} alt="" /> : <span className="smx-crest-fallback">{t.team_code}</span>}
          <div>
            <span className="smx-abbr">{t.team_code}
              <span className={`smx-status ${t.status === "complete" ? "ok" : "warn"}`} data-testid={`sm-status-${t.team_code}`}>{t.status}</span>
            </span>
            <span className="smx-full">{meta?.name || ""}</span>
            <span className="smx-rec">{rec && <b>{rec}</b>}{seed ? ` (${seed})` : ""}{oppCode ? ` · vs ${oppCode} (${home ? "Home" : "Away"})` : ""}</span>
          </div>
        </button>

        <div className="smx-box" data-testid={`sm-totals-${t.team_code}`}>
          <span className="smx-box-label">Team projection (NBACUBE)</span>
          <div className="smx-totals">
            {STATS.map(([k, l]) => (
              <div key={k}><small>{l}</small><b>{t.totals ? f1(t.totals[k]) : "—"}</b><em>x4 {f1(t.x4_totals?.[k])}</em></div>
            ))}
          </div>
        </div>

        <div className="smx-box">
          <span className="smx-box-label">Rotation analysis (240 min) <span className={`smx-240 ${min240ok ? "ok" : "bad"}`} data-testid={`sm-240-${t.team_code}`}>{Math.round(t.minutes_total ?? 0)}/240</span></span>
          <div className="smx-rot-bar"><i className="st" style={{ width: `${(stMin / tot) * 100}%` }} /><i className="bn" style={{ width: `${(bnMin / tot) * 100}%` }} /></div>
          <div className="smx-rot-legend">
            <span>Starters <b>{Math.round(stMin)} min ({((stMin / tot) * 100).toFixed(1)}%)</b></span>
            <span>Bench <b>{Math.round(bnMin)} min ({((bnMin / tot) * 100).toFixed(1)}%)</b></span>
          </div>
        </div>
      </div>

      {(t.warnings || []).map((w) => <div className="smx-warnrow" key={w}><p className="smx-warn"><AlertTriangle size={14} />{w}</p></div>)}

      <div className="smx-controls">
        <div className="smx-subtabs">
          <button type="button" className={tab === "all" ? "is-active" : ""} onClick={() => setTab("all")} data-testid={`sm-tab-all-${t.team_code}`}>All ({players.length})</button>
          <button type="button" className={tab === "starters" ? "is-active" : ""} onClick={() => setTab("starters")} data-testid={`sm-tab-starters-${t.team_code}`}>Starters ({counts.starters})</button>
          <button type="button" className={tab === "bench" ? "is-active" : ""} onClick={() => setTab("bench")} data-testid={`sm-tab-bench-${t.team_code}`}>Bench ({counts.bench})</button>
        </div>
        <div className="smx-search">
          <button type="button" className={`smx-details-btn ${details ? "on" : ""}`} onClick={onToggleDetails} data-testid="sm-details-toggle">{details ? "Hide model details" : "Show model details"}</button>
          <Search size={14} color="#6b88aa" />
          <input placeholder="Search player…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`sm-search-${t.team_code}`} />
        </div>
      </div>

      <div className="smx-tablewrap">
        <table className="smx-table">
          <thead>
            <tr>
              <th className="l">Player</th>
              <th className="l">Pos</th>
              <th className="l">Role</th>
              <th className={sort.key === "gp" ? "sorted" : ""} onClick={() => setSortKey("gp")}>GP</th>
              <th className={sort.key === "min" ? "sorted" : ""} onClick={() => setSortKey("min")}>Min</th>
              <th className="l">Band</th>
              {details && ["PTS/m", "REB/m", "AST/m", "3PM/m"].map((l) => <th key={l}>{l}</th>)}
              {STATS.map(([k, l]) => <th key={l} className={sort.key === k ? "sorted" : ""} onClick={() => SORTABLE[k] && setSortKey(k)}>{l}</th>)}
            </tr>
          </thead>
          <tbody>{shown.map((p) => <PlayerRow key={p.player_id} p={p} details={details} />)}</tbody>
        </table>
      </div>

      <p className="smx-meta-line">
        Season split ({hs.games ?? 0} games): <b>S {f1(hs.starter_minutes)} / B {f1(hs.bench_minutes)}</b> · Opp. win odds <b>{t.opp_win_odds?.toFixed(2) ?? "—"}</b> · band <b>{t.target_band || "—"}</b> · OUT from <b>{t.out_source}</b>
        {t.factors && <> · Market factors {["pts", "reb", "ast", "fg3m"].map((m) => `${m === "fg3m" ? "3PM" : m.toUpperCase()} ${t.factors[m].toFixed(3)}`).join(" · ")}</>}
      </p>
    </section>
  );
}
